class AutoComplete {
  /**
   * @param {string} id - ID del elemento input.
   * @param {Array<Object>} data
   * @param {number} maxItems - Número máximo de items a mostrar en el dropdown.
   */
  constructor(id, data, maxItems = 10) {
    this.element = document.getElementById(id);
    if (!this.element) {
      console.error(`Input element with ID '${id}' not found.`);
      return;
    }

    this.data = data;
    this.maxItems = maxItems;
    this.dropdown = null; //Aqui se guardara el menu desplegable
    this.highlightedIndex = -1; //Indice del item actualmente resaltado, empieza en -1 porque no hay ninguno resaltado al inicio
    this.isVisible = false;
    this.timeoutId = null; // Para el debounce, sirve para no hacer la búsqueda en cada pulsación de tecla, sino esperar un momento después de que el usuario deje de escribir

    // Referencias a las funciones vinculadas (bind) para poder remover los listeners
    this.boundKeyDown = this.handleKeyDown.bind(this); //Cuando el usuario presione una tecla, quiero que mi clase sepa que la función es suya y no del input.
    this.boundKeyUp = this.handleKeyUp.bind(this); //Cuando el usuario suelte una tecla, quiero que mi clase sepa que la función es suya y no del input.
    this.boundClickOutside = this.hideDropdown.bind(this); //Cuando el usuario haga click fuera del input o del dropdown, quiero que mi clase sepa que la función es suya y no de otro elemento.
    this.boundDblClick = this.showDropdown.bind(this); //Cuando el usuario haga doble click en el input, quiero que mi clase sepa que la función es suya y no de otro elemento.

    this.init(); //Todo esta listo, ahora enciende el sistema
  }

  /** Inicializa el componente y añade los event listeners. */
  init() {
    // Asegurar que el contenedor padre sea relativo para posicionar el dropdown
    if (this.element.parentNode) {
      this.element.parentNode.style.position = "relative";
    }

    this.element.setAttribute("autocomplete", "off"); //No quiero que Chrome me meta sus sugerencias, solo las mías.
    //Conecto los botones del teclado y el doble click para que mi autocompletado sepa cuándo actuar.
    this.element.addEventListener("keydown", this.boundKeyDown);
    this.element.addEventListener("keyup", this.boundKeyUp);
    this.element.addEventListener("dblclick", this.boundDblClick);

    // Ocultar dropdown al hacer click fuera
    document.addEventListener("click", this.boundClickOutside);

    this.createDropdown(); //Llama a otra función, que se encarga de crear el menú desplegable, para mantener el código organizado y modular.
  }

  /** Crea el contenedor del dropdown. */
  createDropdown() {
    this.dropdown = document.createElement("div"); //crea un <div> vacío en memoria.
    this.dropdown.className = "autocomplete_dropdown"; //Le añadimos una clase.

    // Estilos iniciales
    Object.assign(this.dropdown.style, {
      position: "absolute",
      border: "1px solid #ccc",
      background: "#fff",
      width: this.element.offsetWidth + "px", //Hace que el dropdown tenga el mismo ancho que el input.
      maxHeight: "200px",
      overflowY: "auto", // Si hay muchos resultados, se podrá hacer scroll.
      display: "none",
      zIndex: "1000", // Asegura que el dropdown esté por encima de otros elementos.
      top: this.element.offsetTop + this.element.offsetHeight + "px", //Posiciona el dropdown justo debajo del input.
      left: this.element.offsetLeft + "px", //Posiciona el dropdown alineado a la izquierda del input.
    });

    this.element.parentNode.appendChild(this.dropdown);
  }

  /** Muestra u oculta el dropdown con los resultados filtrados. */
  showDropdown() {
    // Implementación de debounce para limitar la frecuencia de la búsqueda
    if (this.timeoutId) clearTimeout(this.timeoutId);

    this.timeoutId = setTimeout(() => { // Esta función se ejecutará 200ms después de la última pulsación de tecla
      const val = this.element.value.trim();
      this.highlightedIndex = -1; // Reinicia el índice al buscar
      // Si el input está vacío, oculta el dropdown
      if (!val) {
        this.dropdown.innerHTML = "";
        this.isVisible = false;
        this.dropdown.style.display = "none";
        return;
      }

      // Filtrar y limitar resultados

      const results = this.data
        .filter((item) =>
          item.nombre?.toLowerCase().includes(val.toLowerCase()) // Busca coincidencias en el campo 'nombre', ignorando mayúsculas y minúsculas
        )
        .slice(0, this.maxItems);

      // Usar DocumentFragment para mejor rendimiento del DOM
      const fragment = document.createDocumentFragment();

      results.forEach((item, index) => {
        const div = document.createElement("div");
        div.textContent = item.nombre;
        div.dataset.id = item.id;

        Object.assign(div.style, {
          padding: "5px",
          cursor: "pointer",
        });

        div.addEventListener("mouseover", () =>
          this.highlightItem(index, false)
        );
        div.addEventListener("mousedown", (e) => {
          e.preventDefault(); // Previene la pérdida de foco del input
          this.selectItem(item.nombre, item.id);
        });

        fragment.appendChild(div);
      });

      this.dropdown.innerHTML = ""; // Limpiar antes de insertar
      this.dropdown.appendChild(fragment);

      this.isVisible = true;
      this.dropdown.style.display = results.length ? "block" : "none"; // Solo mostrar si hay resultados
    }, 200); // Debounce de 200ms
  }

  /** Oculta el dropdown. */
  hideDropdown(e) {
    // Si el evento existe y el clic fue dentro del input, no ocultar.
    if (e && (e.target === this.element || this.dropdown.contains(e.target))) {
      return;
    }

    if (this.dropdown) {
      this.dropdown.style.display = "none";
    }
    this.isVisible = false;
    this.highlightedIndex = -1;
  }

  /** Selecciona el elemento y actualiza el input. */
  selectItem(value, id) {
    this.element.value = value;
    this.element.dataset.id = id;
    this.element.dispatchEvent(new Event("select", { bubbles: true }));
    //console.log("ID seleccionado:", id);
    this.hideDropdown();
  }

  /** Resalta un item en el dropdown. */
  highlightItem(index, fromKeyboard = false) {
    const children = Array.from(this.dropdown.children); // Obtener los elementos del dropdown

    // Asegurar que el índice esté dentro de los límites
    if (index < 0) {
      this.highlightedIndex = -1;
      return;
    }
    if (index >= children.length) {
      this.highlightedIndex = 0; // O -1 para volver al input
    }

    this.highlightedIndex = index;

    children.forEach((child, i) => {
      child.style.background = i === this.highlightedIndex ? "#bde4ff" : "#fff";
    });

    // Desplazar el elemento para que sea visible
    const highlightedEl = children[this.highlightedIndex];
    if (highlightedEl) {
      highlightedEl.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }

  /** Maneja las pulsaciones de tecla (KeyDown). */
  handleKeyDown(e) {
    if (!this.isVisible || this.dropdown.children.length === 0) {
      // Mostrar dropdown al presionar flecha abajo cuando el input no está vacío
      if (e.key === "ArrowDown" && this.element.value.trim().length > 0) {
        this.showDropdown();
        e.preventDefault();
      }
      return;
    }

    const childrenCount = this.dropdown.children.length;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        // Mover abajo, ciclar al inicio
        let nextDown = this.highlightedIndex + 1;
        if (nextDown >= childrenCount) {
          nextDown = 0;
        }
        this.highlightItem(nextDown, true);
        break;

      case "ArrowUp":
        e.preventDefault();
        // Mover arriba, ciclar al final
        let nextUp = this.highlightedIndex - 1;
        if (nextUp < 0) {
          nextUp = childrenCount - 1;
        }
        this.highlightItem(nextUp, true);
        break;

      case "Enter":
        e.preventDefault();
        if (this.highlightedIndex >= 0) {
          const itemEl = this.dropdown.children[this.highlightedIndex];
          this.selectItem(itemEl.textContent, itemEl.dataset.id);
        }
        break;

      case "Escape":
        this.hideDropdown();
        break;

      case " ":
        e.preventDefault();
        if (this.highlightedIndex >= 0) {
          const itemEl = this.dropdown.children[this.highlightedIndex];
          this.selectItem(itemEl.textContent, itemEl.dataset.id);
        } else {
          // si no hay item seleccionado, solo agrega espacio al input
          this.element.value += " ";
        }
        break;

      default:
        break;
    }
  }

  /** Maneja el evento KeyUp. Principalmente para la búsqueda y el debounce. */
  handleKeyUp(e) {
    // Si fue una tecla de navegación, no hacer nada aquí.
    if (["ArrowDown", "ArrowUp", "Enter", "Escape"].includes(e.key)) {
      return;
    }

    // Para todas las demás teclas, iniciar el debounce de búsqueda
    this.showDropdown();
  }

  /** Destruye el autocomplete y limpia los event listeners. */
  destroy() {
    if (this.element) {
      this.element.removeEventListener("keydown", this.boundKeyDown);
      this.element.removeEventListener("keyup", this.boundKeyUp);
      this.element.removeEventListener("dblclick", this.boundDblClick);
      this.element.removeAttribute("data-id");
    }

    document.removeEventListener("click", this.boundClickOutside);

    if (this.dropdown && this.dropdown.parentNode) {
      this.dropdown.parentNode.removeChild(this.dropdown);
    }

    if (this.timeoutId) clearTimeout(this.timeoutId);

    // Eliminar la instancia del mapa si se usa un registro global (ver nota abajo)
  }
}

// **Función de utilidad para mantener el patrón original de 'export function AutoComplete_Create'**
// Este mapa global es necesario para mantener una única instancia por ID.
const AutoCompleteInstances = {};

/** Inicializa el autocomplete sobre un input (punto de entrada externo) */
export function AutoComplete_Create(id, data, maxItems = 10) {
  // Destruye la instancia anterior si ya existe
  if (AutoCompleteInstances[id]) {
    AutoComplete_Destroy(id);
  }

  const instance = new AutoComplete(id, data, maxItems);
  if (instance.element) {
    // Solo guarda si la inicialización fue exitosa
    AutoCompleteInstances[id] = instance;
  }
  return instance;
}

/** Destruye el autocomplete y limpia los event listeners (punto de entrada externo) */
export function AutoComplete_Destroy(id) {
  const instance = AutoCompleteInstances[id];
  if (instance) {
    instance.destroy();
    delete AutoCompleteInstances[id];
  }
}

// exportamos las funciones para usarlas en otros archivos
export default { AutoComplete_Create, AutoComplete_Destroy };
